<script setup>
// Vue twin of <x-availability-calendar>
// (resources/views/components/availability-calendar.blade.php +
// App\View\Components\AvailabilityCalendar). It emits the same markup and class
// hooks and carries no styling of its own: components/availability-calendar.scss
// styles both implementations. Server-rendered booking pages keep using
// <x-availability-calendar>; the search page's pop-out panel uses this.
//
// The one deliberate structural divergence is month navigation, and it is not a
// parity gap (see docs/frontend-components.md): the Blade side is handed every
// month up front, renders them all and lets availability-calendar.js swap which
// is .current, whereas this fetches one month at a time from the availability
// API. So this renders exactly one month section, always .current, and the root
// carries .enhanced permanently - on the Blade side that class means "the script
// is running", here it means "this is always the enhanced form", which is what
// keeps the nav chevrons visible.
//
// None of the Blade side's data attributes are carried:
// data-availability-calendar, data-days, data-calendar-prev/-next and data-date
// are how availability-calendar.js finds and drives its own calendars, and
// app.js imports that script on every page. The stylesheet selects on none of
// them, so withholding them decouples the two systems completely at no cost.
// data-month is kept - nothing on either side queries it.
//
// Selection is a native radio group, as in Blade: the chosen day is a visually
// hidden radio inside its label, so :has(input:checked) drives the selected look
// and keyboard/screen-reader behaviour comes from the control. Blade's `checked`
// becomes v-model. Range highlighting (days > 1) is classes only - .in_range for
// the committed stay, .preview for the hover, both excluding the start date,
// which carries its own checked look.
//
// Where the Blade constructor throws, this logs a console error and degrades - a
// throw in setup() takes down the widget's whole app root, not one render.
import { ref, computed, watch, useId } from 'vue';
import SvgIcon from '@/components/SvgIcon.vue';
import { formatPrice } from '@/shared/formatPrice.js';

const props = defineProps({
    // { "YYYY-MM-DD": { price, variant_id } } for one month. Availability is
    // presence-based: a date absent from the map is unavailable. This is the
    // availability API's own shape, not Blade's nested months map.
    dates: {
        type: Object,
        required: true,
    },
    month: {
        type: String,
        required: true,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    // Calendar days the stay covers, both ends included; 1 = single-date mode.
    // Blade's name and semantics - a caller holding hotel nights passes
    // nights + 1.
    days: {
        type: Number,
        default: 1,
    },
    // The chosen ISO date. A valid date that isn't available is ignored, as in
    // Blade - stale availability data is a data condition, not a call-site typo.
    modelValue: {
        type: String,
        default: null,
    },
    // Radio group name. Defaults to a generated id so two calendars on one page
    // (a stacked second panel) cannot share a group by accident.
    name: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['update:modelValue', 'month-change']);

const MONTH_PATTERN = /^\d{4}-\d{2}$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const DAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const LONG_DATE = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
});

const groupName = props.name ?? useId();

// Blade throws on days < 1. A call-site decision, so checked once at setup.
if (props.days < 1) {
    console.error(
        `[AvailabilityCalendar] days must be 1 or more, got [${props.days}]. Using 1.`
    );
}

const days = computed(() => (props.days < 1 ? 1 : props.days));

// month and modelValue both change at runtime, so their format checks live in
// watchers rather than running once at setup.
const monthValid = computed(() => MONTH_PATTERN.test(props.month));

watch(() => props.month, (month) => {
    if (! MONTH_PATTERN.test(month)) {
        console.error(
            `[AvailabilityCalendar] month must be YYYY-MM, got [${month}]. Rendering no grid.`
        );
    }
}, { immediate: true });

watch(() => props.modelValue, (value) => {
    if (value !== null && ! DATE_PATTERN.test(value)) {
        console.error(
            `[AvailabilityCalendar] the selected date must be YYYY-MM-DD, got [${value}]. Ignoring it.`
        );
    }
}, { immediate: true });

// Writable proxy so the native radios get Vue's own group semantics while the
// selection lives with the caller.
const selectedDate = computed({
    get: () => (props.modelValue !== null && DATE_PATTERN.test(props.modelValue) ? props.modelValue : null),
    set: (value) => emit('update:modelValue', value),
});

const year = computed(() => parseInt(props.month.slice(0, 4), 10));
const monthIndex = computed(() => parseInt(props.month.slice(5, 7), 10) - 1);

const monthLabel = computed(() => {
    if (! monthValid.value) {
        return '';
    }

    return new Date(year.value, monthIndex.value, 1)
        .toLocaleString('en-GB', { month: 'long', year: 'numeric' });
});

// Blade disables prev on the first month it was handed and next on the last.
// There is no last here - the API will serve any month - so only the backward
// end-stop exists, and it is "don't navigate into the past".
const isCurrentMonth = computed(() => {
    const now = new Date();

    return monthValid.value
        && year.value === now.getFullYear()
        && monthIndex.value === now.getMonth();
});

function pad(n) {
    return String(n).padStart(2, '0');
}

function formatDate(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// "Monday 15 August 2026", matching Blade's `l j F Y`. en-GB's own long format
// puts a comma after the weekday, so both sides announce a day identically.
function longDate(d) {
    return LONG_DATE.format(d).replace(',', '');
}

/*
 * The shared formatter, not a rounded one of this calendar's own.
 *
 * It used to round to whole pounds, which was invisible while every catalogue price was a
 * whole number - the last minute discount is the first thing to produce pence, and it made a
 * day cell read "£56" for a treatment the card and the basket both priced at £55.80. Rounded
 * UP, so the calendar quoted 20p more than the customer was charged.
 *
 * Empty string rather than null for an unknown price, which is what the caller below needs:
 * the value goes straight into a cell and into a joined aria-label.
 */
function dayPrice(price) {
    return formatPrice(price) ?? '';
}

// Given a "YYYY-MM-DD" start date, the date strings the stay covers, both ends
// included.
function rangeFrom(dateStr, count) {
    const [y, m, d] = dateStr.split('-').map(Number);

    return Array.from({ length: count }, (_, i) => formatDate(new Date(y, m - 1, d + i)));
}

// One cell per grid slot, Monday-first whole weeks, so the month's lead-in and
// lead-out days from the adjacent months render (greyed and inert) exactly as
// they do in Blade. Their dates carry no price - only this month was fetched -
// which is what makes them inert without a special case.
const cells = computed(() => {
    if (! monthValid.value) {
        return [];
    }

    const y = year.value;
    const m = monthIndex.value;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const leadIn = (new Date(y, m, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const leadOut = (7 - ((leadIn + daysInMonth) % 7)) % 7;

    return Array.from({ length: leadIn + daysInMonth + leadOut }, (_, i) => {
        const date = new Date(y, m, 1 - leadIn + i);
        const dateStr = formatDate(date);

        const outside = date.getMonth() !== m;
        const past = ! outside && date < today;
        // While a month's request is in flight the `dates` map still holds the
        // previous month's availability, so nothing counts as available: it keeps
        // the grid inert and stops a stale price painting onto a day it doesn't
        // belong to.
        const available = ! outside && ! past && ! props.loading && dateStr in props.dates;

        return {
            day: date.getDate(),
            date: dateStr,
            outside,
            past,
            // Deliberately not set while loading: a day nothing is known about
            // yet is not a day known to be unavailable, and .unavailable strikes
            // the number through, which would read as "sold out" across the whole
            // month. Loading days take the .past look instead - dimmed, inert,
            // claiming nothing. (Genuinely past days keep .past regardless; that
            // is true whatever the API comes back with.)
            unavailable: ! outside && ! past && ! available && ! props.loading,
            available,
            price: available ? dayPrice(props.dates[dateStr]?.price ?? null) : null,
            ariaLabel: available
                ? [longDate(date), dayPrice(props.dates[dateStr]?.price ?? null)]
                    .filter(Boolean)
                    .join(', ')
                : null,
        };
    });
});

// ─── Range highlighting ──────────────────────────────────────────────────────

const hoveredDate = ref(null);

// A new month's dates make any in-progress hover preview stale.
watch(() => props.month, () => { hoveredDate.value = null; });

// Everything a stay covers except its start date, which carries its own checked
// or hover look. Empty in single-date mode.
function rangeAfter(start) {
    if (start === null || days.value <= 1) {
        return new Set();
    }

    return new Set(rangeFrom(start, days.value).slice(1));
}

function rangeEnd(start) {
    if (start === null || days.value <= 1) {
        return null;
    }

    const dates = rangeFrom(start, days.value);

    return dates[dates.length - 1];
}

const selectedRange = computed(() => rangeAfter(selectedDate.value));
const previewRange = computed(() => rangeAfter(hoveredDate.value));

// While a preview is live it owns the end marker, matching the vanilla script,
// which repaints range_end for whichever range it drew last.
const activeRangeEnd = computed(() => rangeEnd(hoveredDate.value) ?? rangeEnd(selectedDate.value));

// A cross-month range highlights only its in-month portion: the adjacent month's
// grid isn't fetched for a preview, and Blade excludes its lead-in/out cells from
// the range for the same reason.
function cellClasses(cell) {
    if (cell.outside) {
        return { outside: true };
    }

    return {
        past: cell.past,
        unavailable: cell.unavailable,
        in_range: selectedRange.value.has(cell.date),
        preview: previewRange.value.has(cell.date),
        range_end: cell.date === activeRangeEnd.value,
    };
}

// Hover preview of the range a click would commit, available days only, and only
// when there is a range to show.
function onCellEnter(cell) {
    if (days.value > 1 && cell.available) {
        hoveredDate.value = cell.date;
    }
}

function shiftMonth(delta) {
    if (! monthValid.value) {
        return;
    }

    const d = new Date(year.value, monthIndex.value + delta, 1);

    emit('month-change', `${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
}
</script>

<template>
    <div class="availability_calendar enhanced">
        <section class="availability_calendar_month current" :data-month="month">
            <header class="availability_calendar_nav">
                <button
                    type="button"
                    class="availability_calendar_prev plain"
                    aria-label="Previous month"
                    :disabled="isCurrentMonth"
                    @click="shiftMonth(-1)"
                >
                    <SvgIcon name="chevron_left" />
                </button>

                <span class="availability_calendar_month_label">{{ monthLabel }}</span>

                <button
                    type="button"
                    class="availability_calendar_next plain"
                    aria-label="Next month"
                    @click="shiftMonth(1)"
                >
                    <SvgIcon name="chevron_right" />
                </button>
            </header>

            <ul class="availability_calendar_day_labels" aria-hidden="true">
                <li v-for="label in DAY_LABELS" :key="label">{{ label }}</li>
            </ul>

            <!-- The month's real grid renders throughout: it is derived from
                `month` alone, so its shape is known before availability arrives
                and nothing shifts when the data lands. While loading, `cells`
                marks every day unavailable, so this is the ordinary inert-day
                branch below - .availability_calendar_loading only adds the
                shimmer. No Blade equivalent: that side is server-rendered. -->
            <ul
                class="availability_calendar_grid"
                :class="{ availability_calendar_loading: loading }"
                :aria-busy="loading ? 'true' : undefined"
                @mouseleave="hoveredDate = null"
            >
                <template v-for="cell in cells" :key="cell.date">
                    <li
                        v-if="cell.available"
                        class="availability_calendar_day"
                        :class="cellClasses(cell)"
                        @mouseover="onCellEnter(cell)"
                    >
                        <label>
                            <input
                                v-model="selectedDate"
                                type="radio"
                                :name="groupName"
                                :value="cell.date"
                                :aria-label="cell.ariaLabel"
                            >
                            <span class="day_number">{{ cell.day }}</span>
                            <span class="day_price">{{ cell.price }}</span>
                        </label>
                    </li>

                    <!-- Inert day. Adjacent-month lead-in/out cells are
                        aria-hidden - their dates belong to (and are announced in)
                        their own month's grid. -->
                    <li
                        v-else
                        class="availability_calendar_day"
                        :class="cellClasses(cell)"
                        :aria-hidden="cell.outside ? 'true' : undefined"
                    >
                        <span class="day_number">{{ cell.day }}</span>
                    </li>
                </template>
            </ul>
        </section>
    </div>
</template>
